import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Calendar, Clock, Users } from "lucide-react";
import { BookingSlot } from "@/templates/TemplateInterface";
import {
  BookingDateTimePicker,
  toLocalDateKey,
  parseDurationMinutes,
} from "@/components/booking/BookingDateTimePicker";
import { formatPrice as formatPriceUtil } from "@/utils/currency";
import { addDays } from "date-fns";
import type { BookingCheckoutPayload } from "@/templates/components/StorefrontBookingCheckout";

const BOOKING_TYPE_LABELS: Record<string, string> = {
  car_hire: "Car Hire",
  apartment: "Apartment",
  hotel: "Hotel",
  service: "Service",
  other: "Booking",
};

interface BookingQuickViewProps {
  open: boolean;
  onClose: () => void;
  slot: BookingSlot | null;
  currency?: string;
  themeColors: {
    primaryColor: string;
    secondaryColor?: string;
  };
  onProceedToCheckout: (payload: BookingCheckoutPayload) => void;
}

export function BookingQuickView({
  open,
  onClose,
  slot,
  currency = "NGN",
  themeColors,
  onProceedToCheckout,
}: BookingQuickViewProps) {
  const primary = themeColors.primaryColor;
  const [bookingData, setBookingData] = useState({
    startDate: "",
    endDate: "",
    startTime: "",
    endTime: "",
    quantity: 1,
  });
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
  });

  useEffect(() => {
    if (slot) {
      setBookingData({
        startDate: "",
        endDate: "",
        startTime: "",
        endTime: "",
        quantity: 1,
      });
      setCustomerInfo({
        name: "",
        email: "",
        phone: "",
        address: "",
      });
    }
  }, [slot?.id]);

  if (!slot) return null;

  const formatPrice = (price: number) =>
    formatPriceUtil(price, { currency });

  const durationMinutes = parseDurationMinutes(slot.duration);
  const isMultiDay = durationMinutes >= 24 * 60;
  const needsTime = !isMultiDay;

  const canProceed =
    Boolean(bookingData.startDate) &&
    Boolean(bookingData.endDate) &&
    (!needsTime || Boolean(bookingData.startTime)) &&
    Boolean(customerInfo.name.trim()) &&
    Boolean(customerInfo.email.trim()) &&
    Boolean(customerInfo.phone.trim());

  const handleProceed = () => {
    if (!canProceed) return;
    onProceedToCheckout({ slot, bookingData, customerInfo });
    onClose();
  };

  const handleDateChange = (date: Date | undefined) => {
    if (!date) {
      setBookingData((prev) => ({
        ...prev,
        startDate: "",
        endDate: "",
        startTime: "",
        endTime: "",
      }));
      return;
    }

    const dateStr = toLocalDateKey(date);
    const dayCount = Math.max(1, Math.round(durationMinutes / (24 * 60)));
    const endStr = isMultiDay
      ? toLocalDateKey(addDays(date, dayCount))
      : dateStr;

    setBookingData((prev) => ({
      ...prev,
      startDate: dateStr,
      endDate: endStr,
      startTime: "",
      endTime: "",
    }));
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="flex max-h-[92vh] flex-col gap-0 overflow-hidden rounded-[1.75rem] border-black/5 p-0 sm:max-w-[720px]">
        <DialogHeader className="shrink-0 border-b border-black/5 px-5 py-4 sm:px-6">
          <DialogTitle className="pr-8 font-heading text-lg font-bold tracking-tight sm:text-xl">
            {slot.name}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5 sm:px-6">
          <div className="grid gap-5 sm:grid-cols-[180px_1fr]">
            {slot.images?.[0] ? (
              <div className="aspect-square overflow-hidden rounded-2xl bg-black/[0.03] ring-1 ring-black/5">
                <img
                  src={slot.images[0].url}
                  alt={slot.images[0].alt || slot.name}
                  className="h-full w-full object-cover"
                />
              </div>
            ) : (
              <div
                className="flex aspect-square items-center justify-center rounded-2xl ring-1 ring-black/5"
                style={{ backgroundColor: `${primary}12` }}
              >
                <Calendar className="h-10 w-10" style={{ color: `${primary}55` }} />
              </div>
            )}

            <div className="space-y-3">
              <div
                className="text-2xl font-bold sm:text-3xl"
                style={{ color: primary }}
              >
                {formatPrice(slot.price)}
              </div>
              {slot.description && (
                <p className="text-sm leading-relaxed text-black/50">
                  {slot.description}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <span
                  className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold"
                  style={{ backgroundColor: `${primary}14`, color: primary }}
                >
                  {BOOKING_TYPE_LABELS[slot.bookingType] || slot.bookingType}
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-black/[0.04] px-3 py-1 text-xs font-medium text-black/55">
                  <Clock className="h-3.5 w-3.5" />
                  {slot.duration}
                </span>
                {slot.capacity > 1 && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-black/[0.04] px-3 py-1 text-xs font-medium text-black/55">
                    <Users className="h-3.5 w-3.5" />
                    {slot.capacity} people
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-black/5 bg-black/[0.015] p-4">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h3 className="font-heading text-sm font-bold">
                  Select date & time
                </h3>
                <p className="mt-0.5 text-xs text-black/40">
                  {isMultiDay
                    ? "Choose your check-in date"
                    : "Choose a day, then pick an available time"}
                </p>
              </div>
              {needsTime && !bookingData.startTime && bookingData.startDate && (
                <span className="shrink-0 rounded-full bg-amber-50 px-2.5 py-1 text-[10px] font-semibold text-amber-700">
                  Time required
                </span>
              )}
            </div>
            <BookingDateTimePicker
              availability={slot.availability}
              duration={slot.duration}
              timeInterval={slot.timeInterval || 30}
              startDate={bookingData.startDate}
              endDate={bookingData.endDate}
              startTime={bookingData.startTime}
              endTime={bookingData.endTime}
              themeColors={themeColors}
              onDateChange={handleDateChange}
              onTimeChange={(start, end) => {
                setBookingData((prev) => ({
                  ...prev,
                  startTime: start,
                  endTime: end,
                }));
              }}
            />
            {slot.capacity > 1 && (
              <div className="mt-4 space-y-1.5">
                <Label htmlFor="booking-qty" className="text-sm">
                  Quantity
                </Label>
                <Input
                  id="booking-qty"
                  type="number"
                  min={1}
                  max={slot.capacity}
                  value={bookingData.quantity}
                  onChange={(e) =>
                    setBookingData((prev) => ({
                      ...prev,
                      quantity: parseInt(e.target.value, 10) || 1,
                    }))
                  }
                  className="h-11 rounded-full"
                />
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-black/5 bg-black/[0.015] p-4">
            <h3 className="mb-3 font-heading text-sm font-bold">
              Your information
            </h3>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="booking-name" className="text-sm">
                  Full name *
                </Label>
                <Input
                  id="booking-name"
                  value={customerInfo.name}
                  onChange={(e) =>
                    setCustomerInfo((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  placeholder="John Doe"
                  className="h-11 rounded-full"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="booking-email" className="text-sm">
                  Email *
                </Label>
                <Input
                  id="booking-email"
                  type="email"
                  value={customerInfo.email}
                  onChange={(e) =>
                    setCustomerInfo((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                  placeholder="you@example.com"
                  className="h-11 rounded-full"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="booking-phone" className="text-sm">
                  Phone *
                </Label>
                <Input
                  id="booking-phone"
                  type="tel"
                  value={customerInfo.phone}
                  onChange={(e) =>
                    setCustomerInfo((prev) => ({
                      ...prev,
                      phone: e.target.value,
                    }))
                  }
                  placeholder="+234 800 000 0000"
                  className="h-11 rounded-full"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="booking-address" className="text-sm">
                  Address
                </Label>
                <Input
                  id="booking-address"
                  value={customerInfo.address}
                  onChange={(e) =>
                    setCustomerInfo((prev) => ({
                      ...prev,
                      address: e.target.value,
                    }))
                  }
                  placeholder="Optional"
                  className="h-11 rounded-full"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="shrink-0 border-t border-black/5 px-5 py-4 sm:px-6">
          <Button
            onClick={handleProceed}
            disabled={!canProceed}
            className="h-12 w-full rounded-full text-sm font-semibold text-white hover:!text-white hover:opacity-90"
            style={{ backgroundColor: primary }}
          >
            Proceed to Checkout
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
